import type { RoleContext } from './roles';
import { roleIndicatesSupervisorNurse } from './roles';
import { normalizeRoleId } from './idNormalization';

/**
 * True when the user is a supervisor nurse by position or roles[] (not by generalRole ADMIN alone).
 * Used to extend Ethic team nav and page access to all SN while keeping ETHIC-team-only full-department filters.
 */
export function isSupervisorNurseNavUser(roleContext: RoleContext | null): boolean {
  if (!roleContext) return false;
  if (roleIndicatesSupervisorNurse(roleContext.positionRole)) return true;
  for (const r of roleContext.roles ?? []) {
    if (typeof r !== 'string') continue;
    const u = r.trim().toUpperCase();
    if (u === 'SUPERVISOR_NURSE' || u === 'SN') return true;
    if (roleIndicatesSupervisorNurse(r)) return true;
  }
  return false;
}

export function isHeadWardNurseNavUser(roleContext: RoleContext | null): boolean {
  if (!roleContext) return false;

  const roles = [
    roleContext.generalRole,
    roleContext.positionRole,
    roleContext.departmentRole,
    roleContext.roleId,
    ...(roleContext.roles ?? []),
    ...(roleContext.departmentRoles ?? []).map((role) =>
      typeof role === 'string' ? role : role.role,
    ),
  ];

  return roles.some((role) => {
    if (typeof role !== 'string') return false;
    const normalized = role.trim().toLowerCase().replace(/[-\s]+/g, '_');
    return normalized === 'hwn' || normalized === 'head_ward_nurse' || normalized.includes('head_ward');
  });
}

/**
 * Normal `/report?category=...` links are department-scoped for HWN and LOF/global-scoped for elevated roles.
 * This does not grant dedicated team-page access.
 */
export function canViewDepartmentScopedIncidentReportNav(roleContext: RoleContext | null): boolean {
  if (!roleContext) return false;
  return isPrivilegedIncidentNavRole(roleContext) || isHeadWardNurseNavUser(roleContext);
}

/**
 * Ethic team destination (/team/ethic): admins/managers/auditors, ethic team category, or any supervisor nurse.
 * Data scope on the page still follows team role (full hospital) vs SN LOF (shell + queries).
 */
export function canViewEthicTeamSection(roleContext: RoleContext | null): boolean {
  if (!roleContext) return false;
  const g = roleContext.generalRole?.trim().toUpperCase();
  if (g === 'ADMIN' || g === 'MANAGER' || g === 'AUDITOR') return true;
  const cats = roleContext.teamAllowedCategoryIds ?? [];
  if (cats.includes('ethicSurveys')) return true;
  return isSupervisorNurseNavUser(roleContext);
}

/**
 * ADMIN / MANAGER / AUDITOR (incl. roleId auditor) — same predicate as TeamResultsPage elevated access.
 */
export function isManagerAuditorOrAdminForTeamDashboard(
  roleContext: RoleContext | null,
  isAdmin: boolean,
): boolean {
  if (isAdmin) return true;
  if (!roleContext) return false;
  const roles = [roleContext.generalRole, roleContext.positionRole, ...(roleContext.departmentRoles ?? [])]
    .map((r) => (typeof r === 'string' ? r : (r as { role?: string })?.role ?? '').toLowerCase())
    .filter(Boolean) as string[];
  return (
    roles.includes('manager') ||
    roles.includes('auditor') ||
    (roleContext.roleId?.toLowerCase() ?? '') === 'auditor'
  );
}

/**
 * True when the user may open /team (nurse_notes team dashboard). Must stay aligned with TeamResultsPage.
 */
export function canAccessNurseNotesTeamDashboard(roleContext: RoleContext | null, isAdmin: boolean): boolean {
  if (isManagerAuditorOrAdminForTeamDashboard(roleContext, isAdmin)) return true;
  if (!roleContext) return false;
  const teamAllowedCategoryIds = roleContext.teamAllowedCategoryIds ?? [];
  const hasNNTeamRole = [roleContext.teamRole, ...(roleContext.teamRoles ?? [])]
    .filter(Boolean)
    .map((r) => {
      if (typeof r === 'string') return r.toUpperCase();
      const o = r as { role?: string; teamCode?: string; teamId?: string };
      return (o.role ?? o.teamCode ?? o.teamId ?? '').toUpperCase();
    })
    .includes('NN');
  return teamAllowedCategoryIds.length > 0 && hasNNTeamRole;
}

/**
 * Navbar variant: KPIs plain link, incident reports under "รายงานอุบัติการณ์", team as dropdown.
 * ADMIN / MANAGER / AUDITOR (generalRole) or supervisor nurse (position or roles[]).
 */
export function isPrivilegedIncidentNavRole(roleContext: RoleContext | null): boolean {
  if (!roleContext) return false;
  const g = roleContext.generalRole?.trim().toUpperCase();
  if (g === 'ADMIN' || g === 'MANAGER' || g === 'AUDITOR') return true;
  if (roleIndicatesSupervisorNurse(roleContext.positionRole)) return true;
  for (const r of roleContext.roles ?? []) {
    if (typeof r !== 'string') continue;
    const u = r.trim().toUpperCase();
    if (u === 'SUPERVISOR_NURSE' || u === 'SN') return true;
    if (roleIndicatesSupervisorNurse(r)) return true;
  }
  return false;
}

/**
 * App shell + ComplianceHub: Google Drive "งานคุณภาพ" menu.
 * Align with ComplianceHub qualityWorkDropdown (SN, HWN, global staff, claims admin).
 */
export function canViewQualityWorkNav(roleContext: RoleContext | null, isAdmin: boolean): boolean {
  if (isAdmin) return true;
  if (!roleContext) return false;
  if (isPrivilegedIncidentNavRole(roleContext)) return true;
  if (isSupervisorNurseNavUser(roleContext)) return true;

  const rid = normalizeRoleId(roleContext.roleId);
  if (rid === 'ADMIN' || rid === 'MANAGER' || rid === 'AUDITOR') return true;

  const sources = [
    roleContext.generalRole,
    roleContext.positionRole,
    roleContext.departmentRole,
    ...(roleContext.roles ?? []),
    ...(roleContext.departmentRoles ?? []).map((d) =>
      typeof d === 'string' ? d : String((d as { role?: string }).role ?? ''),
    ),
  ];
  const normalized = sources
    .filter((r): r is string => typeof r === 'string' && !!r.trim())
    .map((r) => r.trim().toLowerCase());
  if (normalized.some((r) => r.includes('supervisor') || r.includes('audit'))) return true;
  if (normalized.some((r) => r === 'admin' || r === 'manager' || r === 'auditor')) return true;
  if (normalized.some((r) => r === 'head_ward_nurse' || r === 'hwn')) return true;
  if (roleContext.departmentRole === 'HEAD_WARD_NURSE' || roleContext.positionRole === 'HEAD_WARD_NURSE') {
    return true;
  }
  if (roleContext.positionRole === 'HWN') return true;
  if ((roleContext.roles ?? []).some((r) => typeof r === 'string' && ['HEAD_WARD_NURSE', 'HWN'].includes(r.toUpperCase()))) {
    return true;
  }
  if (
    (roleContext.departmentRoles ?? []).some((d) => {
      const role = typeof d === 'object' && d && 'role' in d ? String((d as { role?: string }).role) : String(d);
      return role === 'HEAD_WARD_NURSE' || role === 'HWN';
    })
  ) {
    return true;
  }
  return false;
}
